import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Leaf, ShieldCheck, SunDim, Tag } from "lucide-react";

const HERO_IMG =
  "https://customer-assets-agu9un31.emergentagent.net/job_lamp-shop-10/artifacts/35724b7ae2e0cd98_Captura%20de%20pantalla%202026-10-05%20a%20la%28s%29%202.16.51%E2%80%AFp.m..png";

const LINES = ["La arquitectura de la luz,", "diseñada para la calma."];

const FEATURES = [
  { icon: Leaf, label: "Envío en balanza cero" },
  { icon: ShieldCheck, label: "Garantía de 1 año" },
  { icon: SunDim, label: "Luz cálida 2700K regulable" },
];

export const Hero = ({ onExplore, onPromos }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const glowOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0.2]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative overflow-hidden">
      <motion.div
        style={{ opacity: glowOpacity }}
        className="animate-glow pointer-events-none absolute -top-32 right-[10%] h-[480px] w-[480px] rounded-full bg-amber-400/20 blur-[120px] dark:bg-amber-500/15"
      />
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 pb-20 pt-16 sm:pt-24 lg:grid-cols-[1.15fr_1fr] lg:gap-8">
        <div>
          <motion.button
            data-testid="hero-promo-banner"
            onClick={onPromos}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="group relative mb-6 inline-flex items-center gap-3 rounded-full bg-amber-500 py-3 pl-4 pr-6 text-sm font-semibold text-stone-950 shadow-lg shadow-amber-500/40 transition-all duration-300 hover:scale-[1.03] hover:bg-amber-400 hover:shadow-xl hover:shadow-amber-500/50"
          >
            <span className="relative flex h-6 w-6 items-center justify-center rounded-full bg-stone-950/10">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-stone-950/20" />
              <Tag size={13} />
            </span>
            Promoción especial · lámparas en oferta desde $779
            <span className="rounded-full bg-stone-950 px-2.5 py-0.5 font-mono text-[11px] font-bold text-amber-400">
              OFERTA
            </span>
            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </motion.button>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mb-6 font-mono text-xs uppercase tracking-[0.25em] text-amber-700/80 dark:text-amber-400/90"
          >
            Hylasu — esculturas de luz
          </motion.p>
          <h1 className="font-serif text-4xl font-light leading-[1.08] tracking-tight text-stone-900 dark:text-stone-50 sm:text-6xl lg:text-7xl">
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1.05, delay: 0.3 + i * 0.18, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className="mt-6 max-w-md text-base leading-relaxed text-stone-600 dark:text-stone-300"
          >
            Lámparas decorativas con más de 30 años de experiencia mexicana, hechas para transformar tu espacio en un hogar.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-10"
          >
            <button
              data-testid="hero-cta-button"
              onClick={onExplore}
              className="group inline-flex items-center gap-3 rounded-full bg-stone-900 px-8 py-4 text-sm font-medium text-[#FDFBF7] transition-all duration-300 hover:gap-4 hover:bg-stone-800 dark:bg-stone-50 dark:text-stone-900 dark:hover:bg-white"
            >
              Explorar Catálogo
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5" />
            </button>
          </motion.div>
          <motion.ul
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 1.2 }}
            className="mt-14 flex flex-wrap gap-x-8 gap-y-3"
          >
            {FEATURES.map(({ icon: Icon, label }) => (
              <li key={label} className="flex items-center gap-2 text-xs text-stone-500 dark:text-stone-400">
                <Icon size={14} className="text-amber-600 dark:text-amber-400" />
                {label}
              </li>
            ))}
          </motion.ul>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <div className="overflow-hidden rounded-t-[999px] rounded-b-3xl border border-stone-200/80 shadow-2xl shadow-amber-900/10 dark:border-stone-800">
            <motion.img
              src={HERO_IMG}
              alt="Colgante Alada de Hylasu sobre mesa de comedor"
              style={{ y: imgY, scale: 1.18 }}
              className="aspect-[4/5] w-full object-cover"
            />
          </div>
          <motion.div
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, delay: 1.1 }}
            className="absolute -left-4 bottom-10 rounded-2xl border border-stone-200/70 bg-white/80 px-5 py-4 backdrop-blur-xl dark:border-stone-700/60 dark:bg-stone-900/80 sm:-left-10"
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500 dark:text-stone-400">Pieza insignia</p>
            <p className="mt-1 font-serif text-lg text-stone-900 dark:text-stone-50">Colgante Alada</p>
            <p className="mt-0.5 font-mono text-[10px] text-amber-700 dark:text-amber-400">Nogal & Latón · 2700K</p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
